import React, { useState } from 'react';
import { Body } from '@cognite/cogs.js';
import { SeverityTag } from 'components/SeverityTag/SeverityTag';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { ConsistencyRuleView, RuleOperatorLabel } from '../types';
import {
  formatConditionCheckExpression,
  isConsistencyRuleDisabled,
} from '../helpers';
import { useComparisonRuleListSort } from './useComparisonRuleListSort';
import { RuleActions } from './RuleActions';
import { SortableHeader } from './SortableHeader';
import {
  ConditionChecksOnlyBadge,
  ConditionChecksSection,
  DisabledBadge,
  EmptyStateMessage,
  OperatorChip,
  RuleRow,
  StyledTable,
} from './ComparisonRuleList.styles';

export const CONDITION_CHECKS_ONLY_LABEL = 'Condition check failures only';

type ComparisonRuleListProps = {
  rules: ConsistencyRuleView[];
  onEditRule?: (id: string) => void;
  onCloneRule?: (id: string) => void;
  onDeleteRule?: (id: string) => Promise<void>;
  onToggleDisableRule?: (id: string, disable: boolean) => Promise<void>;
  togglingDisableRuleId?: string | null;
  onRunRule?: (id: string) => void;
};

export const ComparisonRuleList = ({
  rules,
  onEditRule,
  onCloneRule,
  onDeleteRule,
  onToggleDisableRule,
  togglingDisableRuleId = null,
  onRunRule,
}: ComparisonRuleListProps) => {
  const [deletingRule, setDeletingRule] = useState<
    ConsistencyRuleView | undefined
  >();
  const { sort, handleSort, displayedRules } = useComparisonRuleListSort(rules);

  return (
    <>
      <StyledTable>
        <thead>
          <tr>
            <SortableHeader
              label="Rule #"
              column="customerDefinedId"
              activeSort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              label="Name"
              column="name"
              activeSort={sort}
              onSort={handleSort}
            />
            <th>Source → Target</th>
            <SortableHeader
              label="Operator"
              column="operator"
              activeSort={sort}
              onSort={handleSort}
            />
            <SortableHeader
              label="Severity"
              column="severity"
              activeSort={sort}
              onSort={handleSort}
            />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayedRules.length === 0 ? (
            <tr>
              <td colSpan={6}>
                <EmptyStateMessage size="small">
                  No comparison rules defined. Create rules to check consistency
                  between schema filters.
                </EmptyStateMessage>
              </td>
            </tr>
          ) : (
            displayedRules.map((rule) => {
              const disabled = isConsistencyRuleDisabled(rule);
              return (
                <React.Fragment key={rule.externalId}>
                  <RuleRow $disabled={disabled} aria-disabled={disabled}>
                    <td>
                      {rule.customerDefinedId && (
                        <Body size="small">{rule.customerDefinedId}</Body>
                      )}
                    </td>
                    <td>
                      <strong>{rule.name}</strong>
                      {disabled && (
                        <DisabledBadge label="Disabled" size="x-small" />
                      )}
                      {rule.conditionChecksOnly && (
                        <ConditionChecksOnlyBadge
                          label={CONDITION_CHECKS_ONLY_LABEL}
                          size="x-small"
                        />
                      )}
                    </td>
                    <td>
                      <Body size="small">
                        {rule.sourceSchemaFilter.name} ({rule.sourceAttribute})
                        <br />→ {rule.targetSchemaFilter.name} (
                        {rule.targetAttribute})
                      </Body>
                    </td>
                    <td>
                      <OperatorChip
                        label={
                          RuleOperatorLabel[rule.operator] ?? rule.operator
                        }
                        size="small"
                      />
                    </td>
                    <td>
                      <SeverityTag severity={rule.severity} />
                    </td>
                    <td>
                      <RuleActions
                        isDisabled={disabled}
                        isToggling={togglingDisableRuleId === rule.externalId}
                        canRun={Boolean(onRunRule) && !disabled}
                        onEdit={() => onEditRule?.(rule.externalId)}
                        onClone={() => onCloneRule?.(rule.externalId)}
                        onDelete={() => setDeletingRule(rule)}
                        onRun={() => onRunRule?.(rule.externalId)}
                        onToggleDisable={() =>
                          onToggleDisableRule?.(rule.externalId, !disabled)
                        }
                      />
                    </td>
                  </RuleRow>
                  {rule.conditionChecks.length > 0 && (
                    <RuleRow $disabled={disabled}>
                      <td colSpan={6}>
                        <ConditionChecksSection>
                          {rule.conditionChecks.map((check) => (
                            <Body size="small" key={check.externalId}>
                              +{' '}
                              {formatConditionCheckExpression(
                                check,
                                rule.sourceSchemaFilter.name,
                                rule.targetSchemaFilter.name
                              )}
                            </Body>
                          ))}
                        </ConditionChecksSection>
                      </td>
                    </RuleRow>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </StyledTable>

      {deletingRule && (
        <ConfirmationDialog
          title="Delete rule"
          message={`Are you sure you want to delete "${deletingRule.name}"?`}
          textOk="Delete"
          buttonTypeOk="destructive"
          onOk={async () => {
            try {
              await onDeleteRule?.(deletingRule.externalId);
              setDeletingRule(undefined);
            } catch {
              // error notification handled upstream in useDataConsistency
            }
          }}
          onClose={() => setDeletingRule(undefined)}
        />
      )}
    </>
  );
};
